import clsx from 'clsx';
import type { FC, CSSProperties } from 'react';
import { Image } from '@/components/raven/Image';
import type { SmallMenuItem } from '@/types/menu';
import { Button } from '@/components/raven/ui/Button';
import { decodeEntities } from '@/utils/post-helpers';
import { ArrowRight } from '@themaven-net/raven-assets/index';

import styles from './styles.module.css';

export type TeamListSection = {
	id: string;
	items: SmallMenuItem[];
	title: string;
};

interface TeamListProps {
	className?: string;
	ctaScreenReaderText?: string;
	ctaUrl?: string;
	iconPriority?: boolean;
	isInNavigation?: boolean;
	sections?: TeamListSection[];
}

const getSectionColumnCount = (count: number): number => {
	if (count % 6 === 0) {
		return 6;
	}

	if (count % 4 === 0) {
		return 4;
	}

	if (count <= 6) {
		return Math.max(1, Math.min(count, 6));
	}

	return 4;
};

export const TeamLists: FC<TeamListProps> = ({
	className,
	ctaScreenReaderText,
	ctaUrl,
	iconPriority = false,
	isInNavigation = false,
	sections,
}) => {
	if (!sections || !Array.isArray(sections) || sections.length === 0) {
		return null;
	}

	const visibleSections = sections.length;
	const notTooManySections = visibleSections < 4;
	const numberOfColumns =
		!isInNavigation && notTooManySections // This only applies to the block version if sections < 4
			? 4
			: getSectionColumnCount(visibleSections);

	return (
		<div
			className={clsx(styles.teamLists, className)}
			style={
				{
					'--columns': numberOfColumns,
				} as CSSProperties
			}
		>
			<div className={styles.teamListsWrapper}>
				{sections && sections.length > 0 && (
					<div
						className={clsx(styles.teamListsContainer, 'team-list-container', {
							[styles.teamListNotTooManySections]:
								notTooManySections && isInNavigation,
							[styles.teamListsContainer4Columns]: numberOfColumns === 4,
							[styles.teamListsContainer6Columns]: numberOfColumns === 6,
						})}
					>
						{sections.map((section) => (
							<div className={styles.teamList} key={section.id}>
								{section.title && (
									<p className={styles.teamListTitle}>{section.title}</p>
								)}
								<ul className={styles.teamListItems}>
									{section.items.map((item) => (
										<li className={styles.teamListItem} key={item.id}>
											<a
												aria-label={`Link to ${item.text}`}
												className={styles.teamListLink}
												href={item.url}
											>
												{item.icon && (
													<Image
														alt=""
														className={styles.menuSectionsListItemLinkIcon}
														height={24}
														priority={iconPriority}
														src={`${item.icon && item.icon ? item.icon : ''}`}
														width={24}
													/>
												)}
												{decodeEntities(item.text)}
											</a>
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				)}
				{ctaUrl && (
					<div className={styles.teamListsCta}>
						<Button href={ctaUrl} variation="link">
							See all teams
							{ctaScreenReaderText && (
								<span className="screen-reader-text">
									{ctaScreenReaderText}
								</span>
							)}
							<ArrowRight />
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
